import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { UserRole } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import type { User as SupabaseUser } from '@supabase/supabase-js';

interface AppUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  avatar?: string;
  team?: string;
  bonusPercentage?: number;
  createdAt: Date;
  status?: 'active' | 'inactive';
  forcePasswordChange?: boolean;
}

interface AuthContextType {
  user: AppUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isViewOnly: boolean;
  login: (email: string, password: string) => Promise<boolean | string>;
  logout: () => void;
  hasPermission: (allowedRoles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function fetchAppUser(supabaseUser: SupabaseUser): Promise<AppUser | null> {
  // Fetch profile
  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', supabaseUser.id)
    .maybeSingle();

  // Fetch role
  const { data: roleData } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', supabaseUser.id)
    .maybeSingle();

  if (!profile) return null;

  // Block inactive users
  if (!profile.is_active) return null;

  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    name: profile.full_name || supabaseUser.email || '',
    role: (roleData?.role as UserRole) || 'agent',
    createdAt: new Date(profile.created_at),
    status: profile.is_active ? 'active' : 'inactive',
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          // Use setTimeout to avoid deadlock with Supabase client
          setTimeout(async () => {
            const appUser = await fetchAppUser(session.user);
            if (appUser) {
              setUser(appUser);
            } else {
              // Inactive user or no profile - sign out
              await supabase.auth.signOut();
              setUser(null);
            }
            setIsLoading(false);
          }, 0);
        } else {
          setUser(null);
          setIsLoading(false);
        }
      }
    );

    // THEN check existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const appUser = await fetchAppUser(session.user);
        if (appUser) {
          setUser(appUser);
        } else {
          await supabase.auth.signOut();
        }
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<boolean | string> => {
    const { data: signInData, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return false;

    // Check login schedule restriction
    const userId = signInData.user?.id;
    if (userId) {
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (roleData?.role) {
        const { data: schedule } = await supabase
          .from('role_login_schedules')
          .select('*')
          .eq('role', roleData.role)
          .eq('is_enabled', true)
          .maybeSingle();

        if (schedule) {
          const tz = schedule.timezone || 'Europe/Sarajevo';
          const now = new Date();
          // Get current time in the configured timezone
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hour: 'numeric',
            hour12: false,
            weekday: 'short',
          });
          const parts = formatter.formatToParts(now);
          const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
          const weekday = parts.find(p => p.type === 'weekday')?.value || '';
          
          // Map weekday abbreviation to ISO day number (1=Mon..7=Sun)
          const dayMap: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
          const currentDay = dayMap[weekday] || 1;
          
          const allowedDays: number[] = (schedule as any).allowed_days || [];
          const startHour: number = (schedule as any).start_hour ?? 0;
          const endHour: number = (schedule as any).end_hour ?? 24;

          if (!allowedDays.includes(currentDay)) {
            await supabase.auth.signOut();
            return 'LOGIN_DAY_RESTRICTED';
          }
          if (hour < startHour || hour >= endHour) {
            await supabase.auth.signOut();
            return 'LOGIN_HOUR_RESTRICTED';
          }
        }
      }
    }

    return true;
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  const hasPermission = useCallback((allowedRoles: UserRole[]): boolean => {
    if (!user) return false;
    return allowedRoles.includes(user.role);
  }, [user]);

  const isViewOnly = user?.role === 'viewer';

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      isViewOnly,
      login,
      logout,
      hasPermission,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
